/**
 * useSignalCollection Hook
 *
 * React hook for collecting user context signals and sending them to the backend.
 * Part of the Travel Context Inference System.
 *
 * @example
 * function MyComponent() {
 *   const { signals, isLoading, error, refetch } = useSignalCollection();
 *
 *   if (isLoading) return <div>Loading...</div>;
 *   if (error) return <div>Error collecting signals</div>;
 *
 *   return (
 *     <div>
 *       User country: {signals?.ipCountry}
 *       Confidence: {signals?.signalConfidence.location}
 *     </div>
 *   );
 * }
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { getApiUrl } from '../utils/apiConfig';

// =============================================================================
// Types (duplicated from backend for frontend independence)
// =============================================================================

type DeviceType = 'mobile' | 'tablet' | 'desktop';
type SignalConfidence = 'high' | 'medium' | 'low' | 'none';
type TravelSiteCategory =
  | 'booking'
  | 'review'
  | 'guide'
  | 'forum'
  | 'airline'
  | 'aggregator'
  | 'visa'
  | 'news';

interface SignalConfidenceAssessment {
  location: SignalConfidence;
  intent: SignalConfidence;
}

export interface CollectedSignals {
  // Location signals
  ipCountry: string | null;
  timezoneCountry: string | null;
  localeCountry: string | null;

  // Intent signals
  referrerDomain: string | null;
  referrerCountryHint: string | null;
  referrerTravelCategory: TravelSiteCategory | null;
  utmCampaign: string | null;
  utmSource: string | null;
  utmContent: string | null;
  utmMedium: string | null;
  destinationParam: string | null;

  // Device signals
  deviceType: DeviceType;
  touchSupport: boolean;
  screenWidth: number | null;
  screenHeight: number | null;

  // Time signals
  serverTime: Date;
  serverHour: number;
  userTimezone: string | null;
  localHourEstimate: number | null;

  // Meta
  signalConfidence: SignalConfidenceAssessment;
  collectedAt: Date;
}

interface RawClientSignals {
  timezone: string | null;
  locale: string | null;
  languages: string[];
  screenWidth: number;
  screenHeight: number;
  touchSupport: boolean;
}

interface SignalCollectionResponse {
  success: boolean;
  signals: CollectedSignals;
  visitorId: string;
  error?: string;
}

export interface UseSignalCollectionReturn {
  signals: CollectedSignals | null;
  visitorId: string | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

export interface UseSignalCollectionOptions {
  /**
   * Whether to collect signals immediately on mount
   * @default true
   */
  autoCollect?: boolean;
  /**
   * Whether to cache results to avoid repeated API calls
   * @default true
   */
  cache?: boolean;
  /**
   * Cache TTL in milliseconds
   * @default 300000 (5 minutes)
   */
  cacheTtl?: number;
  /**
   * Countries the user has viewed on the current page
   */
  countriesViewed?: string[];
}

// =============================================================================
// Client Signal Collection (runs in browser)
// =============================================================================

/**
 * Collect raw signals from browser APIs
 */
function collectClientSignals(): RawClientSignals {
  // Check if we're in a browser environment
  const isBrowser = typeof window !== 'undefined';

  if (!isBrowser) {
    return {
      timezone: null,
      locale: null,
      languages: [],
      screenWidth: 0,
      screenHeight: 0,
      touchSupport: false,
    };
  }

  return {
    timezone: getTimezone(),
    locale: getLocale(),
    languages: getLanguages(),
    screenWidth: getScreenWidth(),
    screenHeight: getScreenHeight(),
    touchSupport: getTouchSupport(),
  };
}

function getTimezone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

function getLocale(): string | null {
  try {
    if (typeof navigator !== 'undefined') {
      return navigator.language || null;
    }
    return null;
  } catch {
    return null;
  }
}

function getLanguages(): string[] {
  try {
    if (typeof navigator !== 'undefined' && navigator.languages) {
      return Array.from(navigator.languages);
    }
    const single = getLocale();
    return single ? [single] : [];
  } catch {
    return [];
  }
}

function getScreenWidth(): number {
  try {
    if (typeof window !== 'undefined' && window.screen) {
      return window.screen.width || 0;
    }
    return 0;
  } catch {
    return 0;
  }
}

function getScreenHeight(): number {
  try {
    if (typeof window !== 'undefined' && window.screen) {
      return window.screen.height || 0;
    }
    return 0;
  } catch {
    return 0;
  }
}

function getTouchSupport(): boolean {
  try {
    if (typeof window === 'undefined') {
      return false;
    }

    return (
      'ontouchstart' in window ||
      navigator.maxTouchPoints > 0 ||
      // @ts-expect-error - msMaxTouchPoints is IE-specific
      navigator.msMaxTouchPoints > 0
    );
  } catch {
    return false;
  }
}

// =============================================================================
// Cache Management
// =============================================================================

interface CachedSignals {
  signals: CollectedSignals;
  visitorId: string;
  cachedAt: number;
}

const CACHE_KEY = 'jetogo_signal_cache';

function getCachedSignals(ttl: number): CachedSignals | null {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return null;
  }

  try {
    const cached = sessionStorage.getItem(CACHE_KEY);
    if (!cached) return null;

    const parsed: CachedSignals = JSON.parse(cached);
    const now = Date.now();

    // Check if cache is still valid
    if (now - parsed.cachedAt > ttl) {
      sessionStorage.removeItem(CACHE_KEY);
      return null;
    }

    // Convert date strings back to Date objects
    parsed.signals.serverTime = new Date(parsed.signals.serverTime);
    parsed.signals.collectedAt = new Date(parsed.signals.collectedAt);

    return parsed;
  } catch {
    return null;
  }
}

function setCachedSignals(signals: CollectedSignals, visitorId: string): void {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return;
  }

  try {
    const cache: CachedSignals = {
      signals,
      visitorId,
      cachedAt: Date.now(),
    };
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(cache));
  } catch {
    // sessionStorage might be full or disabled
  }
}

// =============================================================================
// Hook Implementation
// =============================================================================

/**
 * React hook for collecting user context signals
 */
export function useSignalCollection(
  options: UseSignalCollectionOptions = {}
): UseSignalCollectionReturn {
  const {
    autoCollect = true,
    cache = true,
    cacheTtl = 5 * 60 * 1000, // 5 minutes
    countriesViewed,
  } = options;

  const [signals, setSignals] = useState<CollectedSignals | null>(null);
  const [visitorId, setVisitorId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Track if initial fetch has been done
  const hasFetched = useRef(false);

  /**
   * Fetch signals from the API
   */
  const fetchSignals = useCallback(async () => {
    // Skip if we're not in a browser
    if (typeof window === 'undefined') {
      return;
    }

    // Check cache first
    if (cache) {
      const cached = getCachedSignals(cacheTtl);
      if (cached) {
        setSignals(cached.signals);
        setVisitorId(cached.visitorId);
        return;
      }
    }

    setIsLoading(true);
    setError(null);

    try {
      // Collect client-side signals
      const clientSignals = collectClientSignals();

      // Build request body
      const requestBody = {
        clientSignals,
        pageUrl: window.location.href,
        countriesViewed: countriesViewed || [],
      };

      // Send to API
      const response = await fetch(getApiUrl('/signals/collect'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Include cookies for visitor ID
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error(`Signal collection failed: ${response.status}`);
      }

      const data: SignalCollectionResponse = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Signal collection failed');
      }

      // Convert date strings to Date objects
      const normalizedSignals: CollectedSignals = {
        ...data.signals,
        serverTime: new Date(data.signals.serverTime),
        collectedAt: new Date(data.signals.collectedAt),
      };

      // Update state
      setSignals(normalizedSignals);
      setVisitorId(data.visitorId);

      // Cache results
      if (cache) {
        setCachedSignals(normalizedSignals, data.visitorId);
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error');
      setError(error);
      console.error('Signal collection error:', error);
    } finally {
      setIsLoading(false);
    }
  }, [cache, cacheTtl, countriesViewed]);

  /**
   * Force refetch (bypasses cache)
   */
  const refetch = useCallback(async () => {
    // Clear cache
    if (typeof window !== 'undefined' && window.sessionStorage) {
      sessionStorage.removeItem(CACHE_KEY);
    }
    await fetchSignals();
  }, [fetchSignals]);

  // Auto-collect on mount
  useEffect(() => {
    if (autoCollect && !hasFetched.current) {
      hasFetched.current = true;
      fetchSignals();
    }
  }, [autoCollect, fetchSignals]);

  return {
    signals,
    visitorId,
    isLoading,
    error,
    refetch,
  };
}

// =============================================================================
// Utility Functions
// =============================================================================

/**
 * Get the user's inferred home country from signals
 */
export function getHomeCountry(signals: CollectedSignals | null): string | null {
  if (!signals) return null;

  // If multiple signals agree, use the consensus
  const countries = [
    signals.ipCountry,
    signals.timezoneCountry,
    signals.localeCountry,
  ].filter(Boolean);

  if (countries.length === 0) return null;

  // Count votes
  const votes: Record<string, number> = {};
  for (const country of countries) {
    if (country) {
      votes[country] = (votes[country] || 0) + 1;
    }
  }

  // Find winner
  let best: string | null = null;
  let bestCount = 0;

  for (const [country, count] of Object.entries(votes)) {
    if (count > bestCount) {
      best = country;
      bestCount = count;
    }
  }

  // Return winner if 2+ votes, otherwise IP country
  return bestCount >= 2 ? best : signals.ipCountry;
}

/**
 * Get the user's likely travel destination from signals
 */
export function getDestinationHint(signals: CollectedSignals | null): string | null {
  if (!signals) return null;

  // Priority: explicit param > referrer hint
  return signals.destinationParam || signals.referrerCountryHint || null;
}

/**
 * Check if signals suggest the user is researching travel
 */
export function isTravelIntent(signals: CollectedSignals | null): boolean {
  if (!signals) return false;

  return (
    signals.signalConfidence.intent === 'high' ||
    signals.signalConfidence.intent === 'medium' ||
    signals.referrerTravelCategory !== null ||
    signals.destinationParam !== null
  );
}

/**
 * Check if the user appears to be traveling (IP differs from home country)
 */
export function isLikelyTraveling(signals: CollectedSignals | null): boolean {
  if (!signals) return false;

  const home = getHomeCountry(signals);
  const ip = signals.ipCountry;

  if (!home || !ip) return false;

  return home !== ip;
}

export default useSignalCollection;
